import { ColumnDef } from "@tanstack/react-table";
import {StatusIndicator, Tooltip} from "@nike/eds";
import { FilingPack } from "../../shared/Types";
import DateTimeFormat from "../shared/DateTimeFormat";
import {AlertCircle} from "@nike/nike-design-system-icons";

export type FilingPackTableColumn<T> = ColumnDef<T, any> & {
    header: string | React.ReactNode | (() => React.ReactNode);
    accessorKey: keyof T | string;
    filterLabel?: string;
    filterType?: "select" | "multiselect" | "text" | "date" | "dateRange" | "monthRange" | "month";
    filterOptions?: Array<{ value: string; label: string }>;
    getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>;
    enableHiding?: boolean;
    enableSorting?: boolean;
    filterKey?: string;
};

function mapStatusToIndicator(status?: string | null): "success" | "warning" | "danger" | "neutral" | "inactive" {
    switch (status?.toUpperCase()) {
        case 'RELEASED_FINAL':
            return 'success';
        case 'FAILED':
            return 'danger';
        default:
            return 'inactive';
    }
}

export type StatusFilterOption = { value: string; label: string };

export const mapStatusesToFilterOptions = (statuses: string[]): StatusFilterOption[] =>
    statuses.map((status) => ({ value: status, label: status }));

export const getFilingPackTableColumns = (
    statusOptions: StatusFilterOption[] = [],
): FilingPackTableColumn<FilingPack>[] => [
    {
        header: "Filing Pack ID",
        accessorKey: "reference",
        size: 200,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: "Period",
        accessorKey: "period_id",
        size: 150,
        enableSorting: false,
        filterLabel: "Period",
        filterType: "month",
        filterKey: "period",
        enableColumnFilter: true,
    },
    {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
            <StatusIndicator
                size='x-large'
                label={row.original.status || '—'}
                status={mapStatusToIndicator(row.original.status)}
            />
        ),
        size: 140,
        enableSorting: false,
        filterLabel: "Status",
        filterType: "multiselect",
        filterOptions: statusOptions,
        filterKey: "status",
        enableColumnFilter: true,
    },
    {
        header: "LRN",
        accessorKey: "lrn",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: "MRN",
        accessorKey: "mrn",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: "Created At",
        accessorKey: "created_at",
        cell: ({ row }) => <DateTimeFormat timestamp={row.original.created_at} format="dd/MM/yyyy, HH:mm" />,
        size: 170,
        enableSorting: true,
        enableColumnFilter: false,
    },
    {
        header: "Updated At",
        accessorKey: "updated_at",
        cell: ({ row }) => <DateTimeFormat timestamp={row.original.updated_at} format="dd/MM/yyyy, HH:mm" />,
        size: 170,
        enableSorting: true,
        enableColumnFilter: false,
    },
    {
        header: "Error",
        accessorKey: "error_message",
        cell: ({row}) => {
            return row.original.error_message != null ?
                <Tooltip bodySlot={row.original.error_message}>
                    <AlertCircle />
                </Tooltip> : null;
        },
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
    }
];
